import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { categories } from "@/mocks/videos";
import { useFavorites } from "@/hooks/useFavorites";

const navLinks = [
  { label: "首页", to: "/" },
  { label: "关于", to: "/about" },
];

export default function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { count } = useFavorites();
  const [keyword, setKeyword] = useState("");
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setCategoryOpen(false);
    setMobileOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setCategoryOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const submitSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmed = keyword.trim();
    navigate(trimmed ? `/search?q=${encodeURIComponent(trimmed)}` : "/search");
  };

  const isCategoryActive = location.pathname.startsWith("/category");

  return (
    <header className="sticky top-0 z-50 border-b border-background-200/70 bg-background-50/90 backdrop-blur-md">
      <div className="flex h-16 w-full items-center gap-4 px-4 md:px-8">
        <Link to="/" className="flex shrink-0 items-center gap-2.5">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-primary-500 to-primary-700 text-background-50">
            <i className="ri-play-circle-fill text-xl"></i>
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-heading text-lg font-extrabold tracking-tight text-foreground-950">
              茄子视频
            </span>
            <span className="mt-1 font-label text-[10px] tracking-[0.22em] text-foreground-500">
              EGGPLANT TV
            </span>
          </span>
        </Link>

        <nav className="ml-4 hidden items-center gap-1 md:flex">
          <Link
            to="/"
            className={`rounded-md px-3 py-2 text-sm transition-colors ${
              location.pathname === "/"
                ? "bg-primary-100 font-medium text-primary-800"
                : "text-foreground-700 hover:bg-background-100 hover:text-foreground-950"
            }`}
          >
            首页
          </Link>

          <div className="relative" ref={dropdownRef}>
            <button
              type="button"
              onClick={() => setCategoryOpen((value) => !value)}
              className={`flex cursor-pointer items-center gap-1 rounded-md px-3 py-2 text-sm transition-colors ${
                isCategoryActive
                  ? "bg-primary-100 font-medium text-primary-800"
                  : "text-foreground-700 hover:bg-background-100 hover:text-foreground-950"
              }`}
            >
              分类
              <i
                className={`ri-arrow-down-s-line text-base transition-transform ${
                  categoryOpen ? "rotate-180" : ""
                }`}
              ></i>
            </button>
            {categoryOpen && (
              <div className="absolute left-0 top-full mt-2 w-[320px] rounded-lg border border-background-200 bg-background-50 p-3">
                <p className="px-2 pb-2 font-label text-[11px] uppercase tracking-[0.16em] text-foreground-500">
                  全部分类
                </p>
                <div className="grid grid-cols-2 gap-1">
                  {categories.map((category) => (
                    <Link
                      key={category.id}
                      to={`/category/${category.id}`}
                      className="flex items-center gap-2 rounded-md px-2 py-2 text-sm text-foreground-700 transition-colors hover:bg-primary-50 hover:text-primary-800"
                    >
                      <span className="flex h-7 w-7 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                        <i className={`${category.icon} text-base`}></i>
                      </span>
                      {category.name}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          <Link
            to="/about"
            className={`rounded-md px-3 py-2 text-sm transition-colors ${
              location.pathname === "/about"
                ? "bg-primary-100 font-medium text-primary-800"
                : "text-foreground-700 hover:bg-background-100 hover:text-foreground-950"
            }`}
          >
            关于
          </Link>
        </nav>

        <div className="ml-auto flex items-center gap-2 md:gap-3">
          <form
            onSubmit={submitSearch}
            className="hidden items-center rounded-full border border-background-200 bg-background-100 pl-3 pr-1.5 py-1.5 transition-colors focus-within:border-primary-300 focus-within:bg-background-50 lg:flex"
          >
            <i className="ri-search-line text-base text-foreground-500"></i>
            <input
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜索视频、标签或作者"
              className="w-52 bg-transparent px-2 text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
            />
            <button
              type="submit"
              className="cursor-pointer whitespace-nowrap rounded-full bg-primary-500 px-3 py-1.5 text-xs font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              搜索
            </button>
          </form>

          <Link
            to="/favorites"
            className="relative flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border border-background-200 bg-background-50 text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
            aria-label="我的收藏"
          >
            <i className="ri-heart-line text-lg"></i>
            {count > 0 && (
              <span className="absolute -right-1 -top-1 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-accent-500 px-1 font-label text-[10px] font-bold text-foreground-950">
                {count > 99 ? "99+" : count}
              </span>
            )}
          </Link>

          <Link
            to="/search"
            className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border border-background-200 bg-background-50 text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700 lg:hidden"
            aria-label="搜索"
          >
            <i className="ri-search-line text-lg"></i>
          </Link>

          <button
            type="button"
            onClick={() => setMobileOpen((value) => !value)}
            className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border border-background-200 bg-background-50 text-foreground-800 md:hidden"
            aria-label="打开菜单"
          >
            <i className={`${mobileOpen ? "ri-close-line" : "ri-menu-line"} text-xl`}></i>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="border-t border-background-200 bg-background-50 px-4 pb-5 pt-4 md:hidden">
          <form
            onSubmit={submitSearch}
            className="flex items-center rounded-full border border-background-200 bg-background-100 px-3 py-2"
          >
            <i className="ri-search-line text-base text-foreground-500"></i>
            <input
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜索视频、标签或作者"
              className="w-full bg-transparent px-2 text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
            />
          </form>

          <div className="mt-4 flex flex-col gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="flex items-center justify-between rounded-md px-3 py-2.5 text-sm text-foreground-800 hover:bg-background-100"
              >
                {link.label}
                <i className="ri-arrow-right-s-line text-base text-foreground-400"></i>
              </Link>
            ))}
            <Link
              to="/favorites"
              className="flex items-center justify-between rounded-md px-3 py-2.5 text-sm text-foreground-800 hover:bg-background-100"
            >
              我的收藏
              <span className="font-label text-xs text-foreground-500">{count} 条</span>
            </Link>
          </div>

          <p className="mt-4 px-3 font-label text-[11px] uppercase tracking-[0.16em] text-foreground-500">
            分类浏览
          </p>
          <div className="mt-2 grid grid-cols-2 gap-1">
            {categories.map((category) => (
              <Link
                key={category.id}
                to={`/category/${category.id}`}
                className="flex items-center gap-2 rounded-md px-3 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-800"
              >
                <i className={`${category.icon} text-base text-primary-500`}></i>
                {category.name}
              </Link>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}